// services/presenter.ts
//
// Presenter brand + crew + quiz-sharing ops. Deliberately SEPARATE from
// live-session.ts (which owns the live host->join->play loop): nothing here is
// a live path — every read below is a one-time get(), never a subscription, so
// the bandwidth-lean topology holds (crews + profiles are tiny, host-only
// enumerations; a public profile is a single-uid read).
import { db } from '../firebase';
import { ref, get, set, update, push } from 'firebase/database';
import { Crew, CrewRole, PresenterProfile } from '../types/presenter.types';
import { Quiz } from '../types/quiz.types';

const paths = {
  profiles: () => 'presenterProfiles',
  profile: (uid: string) => `presenterProfiles/${uid}`,
  crews: () => 'crews',
  crew: (crewId: string) => `crews/${crewId}`,
  crewMember: (crewId: string, uid: string) => `crews/${crewId}/members/${uid}`,
  quizCrew: (quizId: string) => `quizzes/${quizId}/crewId`,
  quizShare: (quizId: string, uid: string) => `quizzes/${quizId}/sharedWith/${uid}`,
  follows: (presenterUid: string) => `follows/${presenterUid}`,
  follow: (presenterUid: string, uid: string) => `follows/${presenterUid}/${uid}`,
};

// --- profiles ----------------------------------------------------------------

export const getPresenterProfile = async (
  uid: string
): Promise<PresenterProfile | null> => {
  const snap = await get(ref(db, paths.profile(uid)));
  return snap.exists() ? (snap.val() as PresenterProfile) : null;
};

// Full-blob save (the profile IS one small blob; partial updates would leave
// stale nextShow announcements behind). Strips undefined — RTDB rejects it.
export const savePresenterProfile = async (
  uid: string,
  profile: PresenterProfile
): Promise<void> => {
  await set(ref(db, paths.profile(uid)), {
    ...JSON.parse(JSON.stringify(profile)),
    updatedAt: Date.now(),
  });
};

// Called by PresenterManager on promotion so every presenter appears in the
// directory (crew/share pickers) even before they've touched their page.
// No-op if a profile already exists — never clobber someone's brand.
export const seedPresenterProfile = async (
  uid: string,
  name?: string,
  photoURL?: string
): Promise<void> => {
  const existing = await get(ref(db, paths.profile(uid)));
  if (existing.exists()) return;
  await set(ref(db, paths.profile(uid)), {
    name: name || 'Presenter',
    ...(photoURL ? { photoURL } : {}),
    updatedAt: Date.now(),
  });
};

// Host-only enumeration (rules): the presenter directory backing the crew
// member picker and the direct-share picker. One-time get, tiny payload.
export const listPresenterDirectory = async (): Promise<
  Array<{ uid: string } & PresenterProfile>
> => {
  const snap = await get(ref(db, paths.profiles()));
  const data = (snap.val() ?? {}) as Record<string, PresenterProfile>;
  return Object.entries(data)
    .map(([uid, p]) => ({ uid, ...p }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

// --- follows -------------------------------------------------------------------
// The re-engagement list without collecting emails: follows/{presenterUid}/{uid}
// = true. A player toggles ONLY their own entry and reads ONLY their own entry
// (follow-state check is a single-child get); the presenter (or admin) reads
// the whole list — their audience, one-time get, never subscribed.

export const isFollowing = async (
  presenterUid: string,
  uid: string
): Promise<boolean> => {
  const snap = await get(ref(db, paths.follow(presenterUid, uid)));
  return snap.val() === true;
};

export const setFollowing = async (
  presenterUid: string,
  uid: string,
  following: boolean
): Promise<void> => {
  await set(ref(db, paths.follow(presenterUid, uid)), following ? true : null);
};

// Presenter/admin only (rules): how many players opted into "tell me about the
// next show". Shown on HostProfile as the audience count.
export const countFollowers = async (presenterUid: string): Promise<number> => {
  const snap = await get(ref(db, paths.follows(presenterUid)));
  return snap.exists() ? Object.keys(snap.val()).length : 0;
};

// --- crews --------------------------------------------------------------------

// One-time get of every crew (hosts only; the node is tiny). RTDB can't query
// "crews whose members include me", so callers filter with myCrews().
export const listCrews = async (): Promise<Record<string, Crew>> => {
  const snap = await get(ref(db, paths.crews()));
  return (snap.val() ?? {}) as Record<string, Crew>;
};

export const myCrews = (
  crews: Record<string, Crew>,
  uid: string
): Record<string, Crew> =>
  Object.fromEntries(
    Object.entries(crews).filter(([, c]) => Boolean(c.members?.[uid]))
  );

export const createCrew = async (
  name: string,
  ownerUid: string,
  description?: string
): Promise<string> => {
  const crewRef = push(ref(db, paths.crews()));
  const crewId = crewRef.key;
  if (!crewId) throw new Error('Failed to generate crew id');
  await set(crewRef, {
    name: name.trim(),
    ...(description?.trim() ? { description: description.trim() } : {}),
    createdBy: ownerUid,
    createdAt: Date.now(),
    members: { [ownerUid]: 'owner' },
  });
  return crewId;
};

export const renameCrew = async (crewId: string, name: string): Promise<void> => {
  await update(ref(db, paths.crew(crewId)), { name: name.trim() });
};

export const deleteCrew = async (crewId: string): Promise<void> => {
  await set(ref(db, paths.crew(crewId)), null);
};

// Owner/admin: add or change a member. Remove by passing null.
export const setCrewMember = async (
  crewId: string,
  uid: string,
  role: CrewRole | null
): Promise<void> => {
  await set(ref(db, paths.crewMember(crewId, uid)), role);
};

// Any member may remove their OWN entry (rules grant exactly that).
export const leaveCrew = async (crewId: string, uid: string): Promise<void> => {
  await set(ref(db, paths.crewMember(crewId, uid)), null);
};

// --- quiz sharing ---------------------------------------------------------------

// Share/unshare a quiz with ONE crew (null clears). Rules require the caller
// to own the quiz AND be a member of the target crew (admins exempt).
export const setQuizCrew = async (
  quizId: string,
  crewId: string | null
): Promise<void> => {
  await set(ref(db, paths.quizCrew(quizId)), crewId);
};

// Direct per-presenter share toggle (true adds, false removes the entry).
export const setQuizShare = async (
  quizId: string,
  uid: string,
  shared: boolean
): Promise<void> => {
  await set(ref(db, paths.quizShare(quizId, uid)), shared ? true : null);
};

// Client-side mirror of the rules' "can this presenter host this quiz" check
// (own / catalog / direct share / crew share). UI filtering only — the rules
// on correctAnswers + sessions are the actual enforcement.
export const canHostQuiz = (
  quiz: Quiz,
  uid: string,
  crews: Record<string, Crew>
): boolean =>
  quiz.createdBy === uid ||
  quiz.catalog === true ||
  quiz.sharedWith?.[uid] === true ||
  Boolean(quiz.crewId && crews[quiz.crewId]?.members?.[uid]);
